import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Camera, FileText } from "lucide-react";
import { fileToDataUrl } from "@/lib/sales";

export default function MeetingMomModal({ open, lead, onClose, onSaved }) {
  const [photos, setPhotos] = useState([]); // data URLs
  const [remarks, setRemarks] = useState(lead?.meeting?.remarks || "");
  const [mom, setMom] = useState(lead?.meeting?.mom || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (!open) return;
    setErr("");
    setPhotos([]);
    setRemarks(lead?.meeting?.remarks || "");
    setMom(lead?.meeting?.mom || "");
  }, [open, lead]);

  const onFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 5);
    const urls = await Promise.all(arr.map(fileToDataUrl));
    setPhotos((p) => [...p, ...urls].slice(0, 6));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!mom) { setErr("Minutes of meeting is required"); return; }
    setSaving(true);
    try {
      await api.post(`/sales/leads/${lead.id}/meeting-mom`, {
        photos, remarks, mom,
      });
      toast.success("Meeting notes saved");
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !lead) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="mom-modal">
      <div className="w-full max-w-lg iz-card p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Post-meeting</p>
            <h2 className="font-display text-xl text-white">Log outcome & MOM</h2>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Site / Meeting Photos</label>
            <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="mom-photos-label">
              <Camera className="w-4 h-4"/> Add photos
              <input type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="mom-photos-input"/>
            </label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-full h-20 object-cover rounded border border-[#262626]"/>
                    <button type="button" onClick={() => setPhotos(ph => ph.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white">
                      <X className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Remarks</label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="iz-input" data-testid="mom-remarks"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Minutes of Meeting (MOM) *</label>
            <textarea value={mom} onChange={(e) => setMom(e.target.value)} rows={5} className="iz-input" data-testid="mom-text" required/>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="mom-submit">
              {saving ? "Saving…" : "Save MOM"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
